import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Shield, ShieldOff, Mail, MailCheck, KeyRound,
  BookOpen, Award, Clock, CheckCircle2, AlertCircle, Eye, Trash2, UserMinus, UserPlus,
  StickyNote, Save, X,
} from 'lucide-react';
import { api, formatDate, formatDateTime, formatMoney } from '../../lib/api';

const STATUS_STYLE = {
  completed: 'bg-emerald-100 text-emerald-700',
  in_progress: 'bg-blue-100 text-blue-700',
  not_started: 'bg-slate-100 text-slate-600',
};

const PURCHASE_STATUS_STYLE = {
  completed: 'bg-emerald-100 text-emerald-700',
  pending: 'bg-amber-100 text-amber-700',
  failed: 'bg-red-100 text-red-700',
  refunded: 'bg-slate-100 text-slate-600',
};

function StatusBadge({ status, styles = STATUS_STYLE }) {
  const cls = styles[status] || 'bg-slate-100 text-slate-600';
  const label = status.replace(/_/g, ' ');
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold capitalize ${cls}`}>
      {label}
    </span>
  );
}

export default function UserDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyAdmin, setBusyAdmin] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showEnroll, setShowEnroll] = useState(false);

  const load = () => {
    setLoading(true);
    setError(null);
    api(`/api/admin/users/${id}`)
      .then(setData)
      .catch(() => setError('Could not load user.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [id]);

  const unenroll = async (enrollmentId, courseTitle) => {
    if (!confirm(`Unenroll ${data.user.email} from "${courseTitle}"? This will also remove their certificate and progress for this course.`)) return;
    try {
      await api(`/api/admin/enrollments/${enrollmentId}`, { method: 'DELETE' });
      load();
    } catch {
      alert('Failed to unenroll.');
    }
  };

  const sendConfirmation = async (enrollmentId, courseTitle) => {
    if (!confirm(`Email the enrollment confirmation for "${courseTitle}" to ${data.user.email}?`)) return;
    try {
      const res = await api(`/api/admin/enrollments/${enrollmentId}/send-confirmation`, { method: 'POST' });
      alert(`Confirmation email sent to ${res.sent_to}.`);
    } catch (err) {
      const code = err.body?.error;
      alert(
        code === 'email_not_configured' ? 'Email service is not configured on the server.'
        : 'Failed to send the confirmation email.'
      );
    }
  };

  const toggleAdmin = async () => {
    const user = data.user;
    const next = !user.is_admin;
    if (next && !confirm(`Promote ${user.email} to admin?`)) return;
    if (!next && !confirm(`Remove admin from ${user.email}?`)) return;
    setBusyAdmin(true);
    try {
      await api(`/api/admin/users/${id}`, { method: 'PATCH', body: { is_admin: next } });
      load();
    } finally {
      setBusyAdmin(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-slate-500">Loading user…</div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-8 text-center">
        <div className="text-red-600 mb-4">{error || 'User not found.'}</div>
        <Link to="/admin/users" className="text-sm text-brand hover:underline">Back to users</Link>
      </div>
    );
  }

  const { user, enrollments, purchases } = data;

  return (
    <div className="p-4 lg:p-8 max-w-5xl mx-auto">
      {/* Breadcrumb */}
      <Link to="/admin/users" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand mb-5">
        <ArrowLeft size={14} /> All users
      </Link>

      {/* ── User header card ────────────────────────────── */}
      <div className="bg-white rounded-lg border border-slate-200 p-6 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-slate-200 flex items-center justify-center text-xl font-bold text-slate-600 shrink-0">
              {user.full_name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">{user.full_name}</h1>
              <div className="flex items-center gap-1.5 text-sm text-slate-600 mt-0.5">
                {user.email_verified ? (
                  <MailCheck size={13} className="text-emerald-500" />
                ) : (
                  <Mail size={13} className="text-slate-400" />
                )}
                {user.email}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={toggleAdmin}
              disabled={busyAdmin}
              className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg font-medium border transition ${
                user.is_admin
                  ? 'border-rose-200 text-rose-600 hover:bg-rose-50'
                  : 'border-violet-200 text-violet-600 hover:bg-violet-50'
              } disabled:opacity-30`}
            >
              {user.is_admin ? <><ShieldOff size={14} /> Remove admin</> : <><Shield size={14} /> Promote to admin</>}
            </button>
            <button
              onClick={() => setShowReset(true)}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg font-medium border border-slate-200 text-slate-700 hover:bg-slate-50 transition"
            >
              <KeyRound size={14} /> Reset password
            </button>
            {!user.is_admin && (
              <button
                onClick={() => setShowDelete(true)}
                className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg font-medium border border-red-200 text-red-600 hover:bg-red-50 transition"
              >
                <Trash2 size={14} /> Delete user
              </button>
            )}
          </div>
        </div>

        {/* Meta row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-5 border-t border-slate-100">
          <MetaItem label="Role" value={user.is_admin ? 'Admin' : 'Learner'} />
          <MetaItem label="Joined" value={formatDate(user.created_at)} />
          <MetaItem label="Last login" value={user.last_login_at ? formatDateTime(user.last_login_at) : 'Never'} />
          <MetaItem label="Account status" value={user.has_password ? 'Active' : 'Provisional (unclaimed)'} />
        </div>
      </div>

      {/* ── Internal admin notes ────────────────────────── */}
      <AdminNotesCard user={user} onSaved={(fields) =>
        setData((d) => ({ ...d, user: { ...d.user, ...fields } }))} />

      {/* ── Enrollments ─────────────────────────────────── */}
      <Section
        title="Enrollments"
        icon={BookOpen}
        count={enrollments.length}
        action={
          <button
            onClick={() => setShowEnroll(true)}
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg font-medium border border-brand/30 text-brand hover:bg-brand/5 transition"
          >
            <UserPlus size={13} /> Enroll in course
          </button>
        }
      >
        {enrollments.length === 0 ? (
          <EmptyState>No enrollments yet.</EmptyState>
        ) : (
          <div className="divide-y divide-slate-100">
            {enrollments.map((e) => (
              <div key={e.enrollment_id} className="px-5 py-4 flex flex-wrap items-center gap-x-6 gap-y-2">
                <div className="flex-1 min-w-[200px]">
                  <div className="font-medium text-slate-900">{e.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5 capitalize">{e.course_type} course</div>
                </div>
                <StatusBadge status={e.status} />
                {e.course_type === 'async' && (
                  <ProgressBar percent={e.progress_percent} />
                )}
                {e.score != null && (
                  <div className="text-xs text-slate-600">Score: <span className="font-semibold">{e.score}%</span></div>
                )}
                {e.certificate_number && (
                  <div className="flex items-center gap-1 text-xs text-emerald-600">
                    <Award size={12} /> Cert #{e.certificate_number}
                  </div>
                )}
                <div className="text-xs text-slate-400 w-full sm:w-auto sm:text-right flex items-center gap-3">
                  <span>
                    Enrolled {formatDate(e.enrolled_at)}
                    {e.completed_at && <> · Completed {formatDate(e.completed_at)}</>}
                    {e.last_interaction_at && !e.completed_at && <> · Last active {formatDateTime(e.last_interaction_at)}</>}
                  </span>
                  <button
                    onClick={() => sendConfirmation(e.enrollment_id, e.title)}
                    className="inline-flex items-center gap-1 text-slate-500 hover:text-brand hover:bg-brand/5 px-2 py-1 rounded transition"
                    title="Send enrollment confirmation email"
                  >
                    <Mail size={13} />
                  </button>
                  <button
                    onClick={() => unenroll(e.enrollment_id, e.title)}
                    className="inline-flex items-center gap-1 text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded transition"
                    title="Unenroll"
                  >
                    <UserMinus size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* ── Purchases ───────────────────────────────────── */}
      <Section title="Purchases" icon={Clock} count={purchases.length}>
        {purchases.length === 0 ? (
          <EmptyState>No purchases yet.</EmptyState>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-5 py-2.5 font-semibold">Course</th>
                <th className="text-left px-3 py-2.5 font-semibold">Amount</th>
                <th className="text-left px-3 py-2.5 font-semibold">Status</th>
                <th className="text-left px-3 py-2.5 font-semibold hidden sm:table-cell">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {purchases.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50">
                  <td className="px-5 py-3 text-slate-900">{p.course_title}</td>
                  <td className="px-3 py-3 text-slate-700">{formatMoney(p.amount_cents, p.currency)}</td>
                  <td className="px-3 py-3"><StatusBadge status={p.status} styles={PURCHASE_STATUS_STYLE} /></td>
                  <td className="px-3 py-3 text-xs text-slate-500 hidden sm:table-cell">{formatDate(p.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {showReset && (
        <ResetPasswordModal
          userId={id}
          email={user.email}
          onClose={() => setShowReset(false)}
        />
      )}

      {showDelete && (
        <DeleteUserModal
          userId={id}
          email={user.email}
          onClose={() => setShowDelete(false)}
        />
      )}

      {showEnroll && (
        <EnrollModal
          userId={id}
          email={user.email}
          enrolledCourseIds={enrollments.map((e) => e.course_id)}
          onClose={() => setShowEnroll(false)}
          onEnrolled={() => { setShowEnroll(false); load(); }}
        />
      )}
    </div>
  );
}

// Internal, admin-only notes about a person (billing quirks, accommodations,
// support history). Never shown to the learner — the field is only returned by
// the admin user endpoints.
function AdminNotesCard({ user, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(user.admin_notes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Re-sync when the user record reloads underneath an idle card.
  useEffect(() => {
    if (!editing) setDraft(user.admin_notes || '');
  }, [user.admin_notes, editing]);

  const dirty = draft.trim() !== (user.admin_notes || '');

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await api(`/api/admin/users/${user.id}`, {
        method: 'PATCH',
        body: { admin_notes: draft },
      });
      onSaved({
        admin_notes: res.admin_notes ?? null,
        admin_notes_updated_at: res.admin_notes_updated_at ?? null,
        admin_notes_updated_by_name: res.admin_notes_updated_by_name ?? null,
      });
      setEditing(false);
    } catch (err) {
      setError(err.body?.error === 'admin_notes_too_long'
        ? 'Note is too long (20,000 character limit).'
        : 'Could not save the note.');
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setDraft(user.admin_notes || '');
    setError(null);
    setEditing(false);
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 mb-6 overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
        <StickyNote size={16} className="text-slate-400" />
        <h2 className="text-sm font-bold text-slate-700">Internal notes</h2>
        <span className="text-xs text-slate-400">(admin only — not visible to this user)</span>
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            className="ml-auto text-xs px-2.5 py-1.5 rounded-lg font-medium border border-slate-200 text-slate-700 hover:bg-slate-50 transition"
          >
            {user.admin_notes ? 'Edit' : 'Add note'}
          </button>
        )}
      </div>

      <div className="px-5 py-4">
        {editing ? (
          <>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={6}
              autoFocus
              placeholder="Context worth keeping on this account — support history, billing exceptions, accommodations…"
              className="input w-full font-normal"
            />
            {error && <div className="text-xs text-rose-600 mt-2">{error}</div>}
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={save}
                disabled={saving || !dirty}
                className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg font-medium bg-brand text-white hover:bg-brand-dark transition disabled:opacity-40"
              >
                <Save size={14} /> {saving ? 'Saving…' : 'Save note'}
              </button>
              <button
                onClick={cancel}
                disabled={saving}
                className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg font-medium border border-slate-200 text-slate-700 hover:bg-slate-50 transition disabled:opacity-40"
              >
                <X size={14} /> Cancel
              </button>
              {!draft.trim() && user.admin_notes && (
                <span className="text-xs text-slate-500">Saving blank clears the note.</span>
              )}
            </div>
          </>
        ) : user.admin_notes ? (
          <>
            <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{user.admin_notes}</p>
            {user.admin_notes_updated_at && (
              <div className="text-xs text-slate-400 mt-3">
                Updated {formatDateTime(user.admin_notes_updated_at)}
                {user.admin_notes_updated_by_name ? ` by ${user.admin_notes_updated_by_name}` : ''}
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-slate-500">No notes yet.</p>
        )}
      </div>
    </div>
  );
}

function MetaItem({ label, value }) {
  return (
    <div>
      <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{label}</div>
      <div className="text-sm text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}

function Section({ title, icon: Icon, count, action, children }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 mb-6 overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
        <Icon size={16} className="text-slate-400" />
        <h2 className="text-sm font-bold text-slate-700">{title}</h2>
        <span className="text-xs text-slate-400">({count})</span>
        {action && <div className="ml-auto">{action}</div>}
      </div>
      {children}
    </div>
  );
}

function EmptyState({ children }) {
  return <div className="px-5 py-8 text-center text-sm text-slate-500">{children}</div>;
}

function ProgressBar({ percent }) {
  return (
    <div className="flex items-center gap-2 min-w-[100px]">
      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-brand rounded-full transition-all"
          style={{ width: `${percent}%` }}
        />
      </div>
      <span className="text-xs text-slate-500 tabular-nums">{percent}%</span>
    </div>
  );
}

function DeleteUserModal({ userId, email, onClose }) {
  const [confirmEmail, setConfirmEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  async function submit(e) {
    e.preventDefault();
    if (confirmEmail.toLowerCase() !== email.toLowerCase()) {
      setError('Email does not match.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await api(`/api/admin/users/${userId}`, { method: 'DELETE' });
      navigate('/admin/users');
    } catch (err) {
      const code = err.body?.error;
      if (code === 'cannot_delete_admin') {
        setError('Cannot delete an admin user. Remove admin role first.');
      } else {
        setError('Could not delete user.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
        <h2 className="text-lg font-bold text-red-600 mb-1">Delete User</h2>
        <p className="text-sm text-slate-500 mb-2">
          This will permanently delete <strong>{email}</strong> and all their enrollments, certificates, and progress. This cannot be undone.
        </p>
        <p className="text-sm text-slate-500 mb-5">
          Type the user's email to confirm:
        </p>

        <form onSubmit={submit} className="space-y-4">
          <input
            type="text"
            required
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            placeholder={email}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100"
          />
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              <AlertCircle size={14} /> {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || confirmEmail.toLowerCase() !== email.toLowerCase()}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
            >
              {busy ? 'Deleting…' : 'Delete permanently'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ResetPasswordModal({ userId, email, onClose }) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await api(`/api/admin/users/${userId}/reset-password`, {
        method: 'POST',
        body: { new_password: password },
      });
      if (res.error) {
        setError(res.error === 'password_too_short' ? 'Password must be at least 8 characters.' : res.error);
        return;
      }
      setDone(true);
    } catch {
      setError('Could not reset password.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
        <h2 className="text-lg font-bold mb-1">Reset Password</h2>
        <p className="text-sm text-slate-500 mb-5">Set a new password for <strong>{email}</strong></p>

        {done ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 border border-emerald-100 rounded-lg px-4 py-3 text-sm">
              <CheckCircle2 size={16} /> Password has been reset.
            </div>
            <div className="flex justify-end">
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-white bg-brand hover:bg-brand-dark rounded-lg">
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">New Password</label>
              <input
                type="text"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Minimum 8 characters"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
              />
            </div>
            {error && (
              <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                <AlertCircle size={14} /> {error}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">
                Cancel
              </button>
              <button type="submit" disabled={busy} className="px-4 py-2 text-sm font-medium text-white bg-brand hover:bg-brand-dark rounded-lg disabled:opacity-50">
                {busy ? 'Resetting…' : 'Reset Password'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function EnrollModal({ userId, email, enrolledCourseIds, onClose, onEnrolled }) {
  const [courses, setCourses] = useState(null); // null = loading
  const [courseId, setCourseId] = useState('');
  const [note, setNote] = useState('');
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api('/api/admin/courses')
      .then((d) => {
        const list = (d.courses || [])
          .filter((c) => c.status === 'published' && !enrolledCourseIds.includes(c.id));
        setCourses(list);
      })
      .catch(() => { setCourses([]); setError('Could not load courses.'); });
  }, []);

  async function submit(e) {
    e.preventDefault();
    if (!courseId) { setError('Pick a course.'); return; }
    setError(null);
    setBusy(true);
    try {
      await api(`/api/admin/users/${userId}/enroll`, {
        method: 'POST',
        body: { course_id: courseId, note: note.trim() || undefined, notify },
      });
      onEnrolled();
    } catch (err) {
      const code = err.body?.error;
      setError(
        code === 'already_enrolled' ? 'This user is already enrolled in that course.'
        : code === 'course_not_found' ? 'That course no longer exists.'
        : 'Could not enroll the user.'
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
        <h2 className="text-lg font-bold mb-1">Enroll in a course</h2>
        <p className="text-sm text-slate-500 mb-5">
          Grant <strong>{email}</strong> access to a course without payment. This records a $0 comp purchase for the audit trail.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Course</label>
            {courses === null ? (
              <div className="text-sm text-slate-400 py-2">Loading courses…</div>
            ) : courses.length === 0 ? (
              <div className="text-sm text-slate-500 py-2">No published courses available to enroll this user in.</div>
            ) : (
              <select
                required
                value={courseId}
                onChange={(e) => setCourseId(e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
              >
                <option value="">Select a course…</option>
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title} — {c.course_type} ({formatMoney(c.price_cents, c.currency)})
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Note <span className="font-normal text-slate-400">(optional)</span></label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Comped — paid by check / scholarship"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={notify}
              onChange={(e) => setNotify(e.target.checked)}
              className="w-4 h-4 accent-brand"
            />
            <span className="text-sm text-slate-700">Email the learner a confirmation</span>
          </label>

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              <AlertCircle size={14} /> {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || !courseId || courses === null}
              className="px-4 py-2 text-sm font-medium text-white bg-brand hover:bg-brand-dark rounded-lg disabled:opacity-50"
            >
              {busy ? 'Enrolling…' : 'Enroll'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
